import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Alert, Btn, FormInput } from '../components/UI';
import api from '../api/client';

const ALL_COUNTRIES = [
  'Afghanistan','Albania','Algeria','Andorra','Angola','Antigua and Barbuda',
  'Argentina','Armenia','Australia','Austria','Azerbaijan','Bahamas','Bahrain',
  'Bangladesh','Barbados','Belarus','Belgium','Belize','Benin','Bhutan','Bolivia',
  'Bosnia and Herzegovina','Botswana','Brazil','Brunei','Bulgaria','Burkina Faso',
  'Burundi','Cabo Verde','Cambodia','Cameroon','Canada','Central African Republic',
  'Chad','Chile','China','Colombia','Comoros','Congo','Costa Rica','Croatia','Cuba',
  'Cyprus','Czech Republic','Denmark','Djibouti','Dominica','Dominican Republic',
  'Ecuador','Egypt','El Salvador','Equatorial Guinea','Eritrea','Estonia','Eswatini',
  'Ethiopia','Fiji','Finland','France','Gabon','Gambia','Georgia','Germany','Ghana',
  'Greece','Grenada','Guatemala','Guinea','Guinea-Bissau','Guyana','Haiti','Honduras',
  'Hungary','Iceland','India','Indonesia','Iran','Iraq','Ireland','Israel','Italy',
  'Jamaica','Japan','Jordan','Kazakhstan','Kenya','Kiribati','Kuwait','Kyrgyzstan',
  'Laos','Latvia','Lebanon','Lesotho','Liberia','Libya','Liechtenstein','Lithuania',
  'Luxembourg','Madagascar','Malawi','Malaysia','Maldives','Mali','Malta',
  'Marshall Islands','Mauritania','Mauritius','Mexico','Micronesia','Moldova',
  'Monaco','Mongolia','Montenegro','Morocco','Mozambique','Myanmar','Namibia',
  'Nauru','Nepal','Netherlands','New Zealand','Nicaragua','Niger','Nigeria','Norway',
  'Oman','Pakistan','Palau','Palestine','Panama','Papua New Guinea','Paraguay','Peru',
  'Philippines','Poland','Portugal','Qatar','Romania','Russia','Rwanda',
  'Saint Kitts and Nevis','Saint Lucia','Saint Vincent and the Grenadines','Samoa',
  'San Marino','Sao Tome and Principe','Saudi Arabia','Senegal','Serbia','Seychelles',
  'Sierra Leone','Singapore','Slovakia','Slovenia','Solomon Islands','Somalia',
  'South Africa','South Sudan','Spain','Sri Lanka','Sudan','Suriname','Sweden',
  'Switzerland','Syria','Taiwan','Tajikistan','Tanzania','Thailand','Timor-Leste',
  'Togo','Tonga','Trinidad and Tobago','Tunisia','Turkey','Turkmenistan','Tuvalu',
  'Uganda','Ukraine','United Arab Emirates','United Kingdom','United States',
  'Uruguay','Uzbekistan','Vanuatu','Vatican City','Venezuela','Vietnam','Yemen',
  'Zambia','Zimbabwe',
];

/* ── Searchable Country Dropdown ── */
function CountrySelect({ value, onChange }) {
  const [open,   setOpen]   = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  const filtered = ALL_COUNTRIES.filter(c => c.toLowerCase().includes(search.toLowerCase()));

  useEffect(() => {
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setSearch(''); }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  function select(country) { onChange(country); setOpen(false); setSearch(''); }

  return (
    <div style={{ marginBottom: 16, position: 'relative' }} ref={ref}>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#2e3456', marginBottom: 6 }}>Country</label>
      <button type="button" onClick={() => setOpen(v => !v)} style={{ width: '100%', padding: '11px 36px 11px 14px', border: '1.5px solid #e2e6f0', borderRadius: 10, fontSize: 14, color: value ? '#0f1233' : '#9099b4', background: '#fff', textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxSizing: 'border-box', fontFamily: 'inherit' }}>
        <span>{value || 'Select your country'}</span>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}>
          <path d="M4 6l4 4 4-4" stroke="#9099b4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      {open && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 500, background: '#fff', border: '1.5px solid #e2e6f0', borderRadius: 12, boxShadow: '0 8px 24px rgba(15,18,51,.12)', marginTop: 4, overflow: 'hidden' }}>
          <div style={{ padding: '10px 12px', borderBottom: '1px solid #f0f2f8', display: 'flex', alignItems: 'center', gap: 8 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9099b4" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
            <input autoFocus type="text" placeholder="Search countries..." value={search} onChange={e => setSearch(e.target.value)} style={{ flex: 1, border: 'none', outline: 'none', fontSize: 14, color: '#0f1233', background: 'transparent', fontFamily: 'inherit' }} />
            {search && <button type="button" onClick={() => setSearch('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9099b4', fontSize: 16, padding: 0 }}>×</button>}
          </div>
          <div style={{ maxHeight: 220, overflowY: 'auto' }}>
            {filtered.length === 0
              ? <div style={{ padding: 16, textAlign: 'center', color: '#9099b4', fontSize: 14 }}>No countries found</div>
              : filtered.map(country => (
                  <div key={country} onClick={() => select(country)} style={{ padding: '10px 14px', fontSize: 14, cursor: 'pointer', color: country === value ? '#4f6ef7' : '#1a1f5e', background: country === value ? '#eef1ff' : 'transparent', fontWeight: country === value ? 600 : 400, borderBottom: '1px solid #f8f9fc' }}
                    onMouseEnter={e => { if (country !== value) e.currentTarget.style.background = '#f8f9fc'; }}
                    onMouseLeave={e => { if (country !== value) e.currentTarget.style.background = 'transparent'; }}>
                    {country}
                  </div>
                ))
            }
          </div>
        </div>
      )}
    </div>
  );
}

/* ── OTP 6-box Input ── */
function OTPInput({ value, onChange }) {
  const inputs = useRef([]);
  const digits = value.split('');

  function handleKey(i, e) {
    if (e.key === 'Backspace') {
      const next = [...digits];
      if (next[i]) { next[i] = ''; onChange(next.join('')); }
      else if (i > 0) inputs.current[i - 1]?.focus();
      return;
    }
    if (e.key === 'ArrowLeft'  && i > 0) { inputs.current[i - 1]?.focus(); return; }
    if (e.key === 'ArrowRight' && i < 5) { inputs.current[i + 1]?.focus(); return; }
  }

  function handleChange(i, e) {
    const val = e.target.value.replace(/\D/g, '').slice(-1);
    if (!val) return;
    const next = [...digits]; next[i] = val;
    onChange(next.join(''));
    if (i < 5) inputs.current[i + 1]?.focus();
  }

  function handlePaste(e) {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    onChange(pasted.padEnd(6,'').slice(0,6));
    inputs.current[Math.min(pasted.length, 5)]?.focus();
  }

  return (
    <div style={{ display: 'flex', gap: 10, justifyContent: 'center', margin: '20px 0' }}>
      {[0,1,2,3,4,5].map(i => (
        <input key={i} ref={el => inputs.current[i] = el}
          type="text" inputMode="numeric" maxLength={1}
          value={digits[i] || ''}
          onChange={e => handleChange(i, e)}
          onKeyDown={e => handleKey(i, e)}
          onPaste={handlePaste}
          style={{ width: 46, height: 54, textAlign: 'center', fontSize: 22, fontWeight: 700, fontFamily: "'Sora',sans-serif", border: `2px solid ${digits[i] ? '#4f6ef7' : '#e2e6f0'}`, borderRadius: 12, outline: 'none', background: digits[i] ? '#eef1ff' : '#fff', color: '#1a1f5e', caretColor: 'transparent', transition: 'all .15s' }}
          onFocus={e => e.target.style.borderColor = '#4f6ef7'}
          onBlur={e  => e.target.style.borderColor = digits[i] ? '#4f6ef7' : '#e2e6f0'}
        />
      ))}
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   MAIN SIGNUP COMPONENT
══════════════════════════════════════════════════════ */
export default function Signup() {
  const { signup } = useAuth();
  const navigate   = useNavigate();
  const [searchParams] = useSearchParams();

  /* Silently read referral code from URL — ?ref=QVXXXXXXXX */
  const refCode = searchParams.get('ref') || null;

  const [form, setForm]         = useState({ name:'', email:'', password:'', country:'' });
  const [errors, setErrors]     = useState({});
  const [apiError, setApiError] = useState('');
  const [loading, setLoading]   = useState(false);
  const [showPass, setShowPass] = useState(false);

  /* OTP state */
  const [otpScreen,  setOtpScreen]  = useState(false);
  const [otp,        setOtp]        = useState('');
  const [otpError,   setOtpError]   = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [resending,  setResending]  = useState(false);
  const [resendMsg,  setResendMsg]  = useState('');
  const [countdown,  setCountdown]  = useState(0);

  const handle = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (countdown <= 0) return;
    const t = setInterval(() => setCountdown(c => c - 1), 1000);
    return () => clearInterval(t);
  }, [countdown]);

  function validate() {
    const e = {};
    if (!form.name.trim() || form.name.length < 2) e.name = 'Name must be at least 2 characters.';
    if (!form.email)                                e.email = 'Email is required.';
    if (form.password.length < 8)                  e.password = 'Password must be at least 8 characters.';
    if (!/[A-Z]/.test(form.password))              e.password = 'Must contain an uppercase letter.';
    if (!/[0-9]/.test(form.password))              e.password = (e.password||'') + ' Must contain a number.';
    return e;
  }

  async function submitSignup(e) {
    e.preventDefault();
    setApiError('');
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    setLoading(true);
    try {
      /* Silently resolve referral code to a user_id before signup */
      let referred_by = null;
      if (refCode) {
        try {
          const { data } = await api.get(`/ref/${refCode}`);
          if (data.user_id) referred_by = data.user_id;
        } catch { /* ignore — referral tracking is optional */ }
      }

      const result = await signup({ ...form, ...(referred_by ? { referred_by } : {}) });
      if (result.needsVerification) {
        setOtpScreen(true);
        setCountdown(60);
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.errors?.[0]?.msg || 'Signup failed. Please try again.';
      setApiError(msg);
    } finally {
      setLoading(false);
    }
  }

  async function submitOTP(e) {
    e.preventDefault();
    if (otp.length < 6) { setOtpError('Please enter all 6 digits.'); return; }
    setOtpError(''); setOtpLoading(true);
    try {
      const { data } = await api.post('/auth/verify-otp', { email: form.email, otp });
      localStorage.setItem('ef_token', data.token);
      localStorage.setItem('ef_user',  JSON.stringify(data.user));
      navigate('/dashboard');
    } catch (err) {
      const d = err.response?.data;
      setOtpError(d?.error || 'Invalid code. Please try again.');
      if (d?.expired) { setOtp(''); setCountdown(0); }
    } finally {
      setOtpLoading(false);
    }
  }

  async function resendOTP() {
    setResending(true); setResendMsg(''); setOtpError('');
    try {
      await api.post('/auth/resend-verification', { email: form.email });
      setResendMsg('New code sent! Check your inbox.');
      setOtp(''); setCountdown(60);
    } catch {
      setOtpError('Failed to resend. Please try again.');
    } finally { setResending(false); }
  }

  /* ── OTP Screen ── */
  if (otpScreen) {
    return (
      <div style={{ minHeight:'100vh', background:'#f8f9fc', display:'flex', alignItems:'center', justifyContent:'center', padding:24, fontFamily:"'DM Sans',sans-serif" }}>
        <div style={{ background:'#fff', borderRadius:24, padding:'48px 36px', maxWidth:420, width:'100%', textAlign:'center', boxShadow:'0 4px 32px rgba(15,18,51,.1)', border:'1px solid #e2e6f0' }}>
          <div style={{ fontFamily:"'Sora',sans-serif", fontSize:22, fontWeight:700, color:'#1a1f5e', marginBottom:24 }}>
            Qu<span style={{ color:'#4f6ef7' }}>vera</span>
          </div>
          <div style={{ width:68, height:68, borderRadius:'50%', background:'#eef1ff', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 20px' }}>
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#4f6ef7" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
              <polyline points="22,6 12,13 2,6"/>
            </svg>
          </div>
          <h2 style={{ fontFamily:"'Sora',sans-serif", fontSize:20, fontWeight:700, color:'#1a1f5e', marginBottom:8 }}>Check your email</h2>
          <p style={{ fontSize:14, color:'#636b89', marginBottom:4 }}>We sent a 6-digit code to</p>
          <p style={{ fontSize:15, fontWeight:700, color:'#1a1f5e', marginBottom:4 }}>{form.email}</p>
          <p style={{ fontSize:13, color:'#9099b4', marginBottom:16 }}>Enter the code below — expires in 10 minutes</p>

          {otpError  && <div style={{ background:'#fee2e2', color:'#dc2626', borderRadius:10, padding:'10px 14px', fontSize:13, marginBottom:12 }}>{otpError}</div>}
          {resendMsg && <div style={{ background:'#dcfce7', color:'#16a34a', borderRadius:10, padding:'10px 14px', fontSize:13, marginBottom:12 }}>{resendMsg}</div>}

          <form onSubmit={submitOTP}>
            <OTPInput value={otp} onChange={setOtp} />
            <Btn loading={otpLoading} type="submit" style={{ width:'100%', justifyContent:'center', padding:'13px', fontSize:15, marginBottom:16 }}>
              Verify Account →
            </Btn>
          </form>

          <div style={{ fontSize:13, color:'#9099b4' }}>
            Didn't receive the code?{' '}
            {countdown > 0 ? (
              <span>Resend in {countdown}s</span>
            ) : (
              <button type="button" onClick={resendOTP} disabled={resending} style={{ background:'none', border:'none', cursor:'pointer', color:'#4f6ef7', fontWeight:600, fontSize:13, padding:0 }}>
                {resending ? 'Sending…' : 'Resend code'}
              </button>
            )}
          </div>
          <button type="button" onClick={() => { setOtpScreen(false); setOtp(''); setOtpError(''); }} style={{ marginTop:20, background:'none', border:'none', cursor:'pointer', color:'#9099b4', fontSize:13 }}>
            ← Back to signup
          </button>
        </div>
      </div>
    );
  }

  /* ── Signup Form ── */
  return (
    <div style={{ display:'flex', minHeight:'100vh', fontFamily:"'DM Sans',sans-serif" }}>
      <style>{`@media(max-width:768px){.signup-left{display:none!important;}.signup-right{padding:36px 20px!important;}}`}</style>

      {/* Left panel */}
      <div className="signup-left" style={{ flex:1, background:'linear-gradient(140deg,#1a1f5e,#1e2878)', display:'flex', alignItems:'center', justifyContent:'center', padding:'60px 48px' }}>
        <div style={{ maxWidth:400 }}>
          <div style={{ fontFamily:"'Sora',sans-serif", fontSize:28, fontWeight:700, color:'#fff', marginBottom:8 }}>
            Qu<span style={{ color:'#93a8ff' }}>vera</span>
          </div>
          <h2 style={{ fontFamily:"'Sora',sans-serif", fontSize:28, fontWeight:700, color:'#fff', marginBottom:12 }}>Start earning today.</h2>
          <p style={{ color:'rgba(255,255,255,.65)', fontSize:15, lineHeight:1.7, marginBottom:36 }}>
            Professionals work on top AI platforms on your behalf. You earn. Available worldwide.
          </p>
          <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginBottom:32 }}>
            {['Appen','Scale AI','Remotasks','Toloka','Prolific','Outlier AI'].map(p => (
              <span key={p} style={{ padding:'6px 14px', borderRadius:20, background:'rgba(255,255,255,.1)', color:'rgba(255,255,255,.8)', fontSize:12, fontWeight:500, border:'1px solid rgba(255,255,255,.15)' }}>{p}</span>
            ))}
          </div>
          <div style={{ background:'rgba(255,255,255,.07)', borderRadius:16, padding:20, border:'1px solid rgba(255,255,255,.1)' }}>
            <div style={{ fontFamily:"'Sora',sans-serif", fontSize:32, fontWeight:700, color:'#fff' }}>$27M+</div>
            <div style={{ fontSize:13, color:'rgba(255,255,255,.5)', marginBottom:10 }}>Total paid out to users</div>
            <span style={{ background:'rgba(22,163,74,.2)', color:'#4ade80', padding:'3px 10px', borderRadius:20, fontSize:12, fontWeight:700 }}>↑ Growing daily</span>
          </div>
        </div>
      </div>

      {/* Right panel */}
      <div className="signup-right" style={{ flex:1, background:'#fff', display:'flex', alignItems:'center', justifyContent:'center', padding:'60px 48px', overflowY:'auto' }}>
        <div style={{ width:'100%', maxWidth:400 }}>
          <Link to="/" style={{ fontSize:13, color:'#9099b4', textDecoration:'none', display:'inline-flex', alignItems:'center', gap:4, marginBottom:24 }}>← Back to home</Link>
          <h1 style={{ fontFamily:"'Sora',sans-serif", fontSize:24, fontWeight:700, color:'#1a1f5e', marginBottom:6 }}>Create your account</h1>
          <p style={{ fontSize:14, color:'#9099b4', marginBottom:24 }}>Join thousands of users earning passively</p>

          <Alert type="error" message={apiError} />

          <form onSubmit={submitSignup}>
            <FormInput label="Full Name" placeholder="e.g. James Mitchell"
              value={form.name} onChange={handle('name')} error={errors.name} required />

            <FormInput label="Email Address" type="email" placeholder="you@example.com"
              value={form.email} onChange={handle('email')} error={errors.email} required />

            {/* Password with show/hide */}
            <div style={{ marginBottom:16 }}>
              <label style={{ display:'block', fontSize:13, fontWeight:600, color:'#2e3456', marginBottom:6 }}>Password</label>
              <div style={{ position:'relative' }}>
                <input type={showPass ? 'text' : 'password'} placeholder="Min 8 chars, 1 uppercase, 1 number"
                  value={form.password} onChange={handle('password')} required
                  style={{ width:'100%', padding:'11px 44px 11px 14px', border:`1.5px solid ${errors.password?'#dc2626':'#e2e6f0'}`, borderRadius:10, fontSize:14, color:'#0f1233', outline:'none', boxSizing:'border-box', fontFamily:'inherit' }}
                />
                <button type="button" onClick={() => setShowPass(v => !v)}
                  style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', padding:0, color:'#9099b4', display:'flex', alignItems:'center' }}>
                  {showPass
                    ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                    : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  }
                </button>
              </div>
              {errors.password && <p style={{ color:'#dc2626', fontSize:12, marginTop:4 }}>{errors.password}</p>}
            </div>

            {/* Password strength */}
            {form.password.length > 0 && (
              <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginTop:-8, marginBottom:14 }}>
                {[[form.password.length>=8,'8+ chars'],[/[A-Z]/.test(form.password),'Uppercase'],[/[0-9]/.test(form.password),'Number']].map(([met,label])=>(
                  <span key={label} style={{ fontSize:11, padding:'2px 8px', borderRadius:20, fontWeight:600, background:met?'#dcfce7':'#f8f9fc', color:met?'#16a34a':'#9099b4', border:`1px solid ${met?'#86efac':'#e2e6f0'}` }}>
                    {met?'✓':'○'} {label}
                  </span>
                ))}
              </div>
            )}

            <CountrySelect value={form.country} onChange={country => setForm(f => ({ ...f, country }))} />

            <Btn loading={loading} type="submit" style={{ width:'100%', justifyContent:'center', padding:13, marginTop:4, fontSize:15 }}>
              {loading ? 'Creating account…' : 'Create Account →'}
            </Btn>
          </form>

          <p style={{ fontSize:14, color:'#9099b4', textAlign:'center', marginTop:20 }}>
            Already have an account? <Link to="/login" style={{ color:'#4f6ef7', fontWeight:600 }}>Log in</Link>
          </p>
          <p style={{ fontSize:11, color:'#c8cedf', textAlign:'center', marginTop:16, lineHeight:1.6 }}>
            By creating an account you agree to our Terms of Service. Your credentials are encrypted and never shared.
          </p>
        </div>
      </div>
    </div>
  );
}
